import {
  Box,
  IconButton,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverProps,
  PopoverTrigger,
  Portal,
  useDisclosure,
  useOutsideClick,
  VisuallyHidden,
} from "@chakra-ui/react";
import { type FocusEvent, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FiBell } from "react-icons/fi";
import { bffApi, useListMyNotificationsQuery, useMarkNotificationsAsPeekedMutation } from "../../autogen/bff-api";
import { fetchFresh } from "../redux/fetchFresh";
import { useAppDispatch } from "../redux/hooks";
import { NOTIFICATION_DROPDOWN_LIMIT, NotificationDropdown } from "./NotificationDropdown";

// `placement` is configurable so the bell can sit on the right of the mobile top bar (bottom-end)
// or next to the logo in the left sidebar (bottom-start), where bottom-end would open off-screen.
export const NotificationBell = ({ placement = "bottom-end" }: { placement?: PopoverProps["placement"] }) => {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const triggerArea = useRef<HTMLDivElement>(null);
  const content = useRef<HTMLElement>(null);
  useOutsideClick({
    ref: content,
    enabled: isOpen,
    handler: (event) => {
      const target = event.target as Element;
      if (!triggerArea.current?.contains(target) && !target.closest?.(TOOLTIP)) onClose();
    },
  });
  const unreadCountId = useId();
  // Unread, not unseen: opening the dropdown or the alert list marks everything peeked, which used
  // to empty this badge for good while the alerts themselves sat unopened. The badge is meant to say
  // "there is something to deal with", so it counts what has not been opened.
  const { data } = useListMyNotificationsQuery({ unread: true }, { refetchOnFocus: true });
  const count = data?.notifications.length ?? 0;
  const dispatch = useAppDispatch();
  const [markAsPeeked] = useMarkNotificationsAsPeekedMutation();

  const peekShownAlerts = async () => {
    const shown = await fetchFresh(
      dispatch(bffApi.endpoints.listMyNotifications.initiate({ limit: NOTIFICATION_DROPDOWN_LIMIT }, { forceRefetch: true })),
    );
    const unpeeked = shown.notifications.filter((n) => !n.peeked).map((n) => n.id);
    if (unpeeked.length > 0) await markAsPeeked({ markNotificationsRequest: { notificationIds: unpeeked } }).unwrap();
  };

  const openAndPeek = () => {
    onOpen();
    void peekShownAlerts().catch(() => undefined);
  };

  return (
    <Popover isOpen={isOpen} onOpen={openAndPeek} onClose={onClose} placement={placement} isLazy>
      <Box ref={triggerArea} position="relative" display="inline-flex">
        <PopoverTrigger>
          <IconButton
            aria-label={t("Alerts") ?? ""}
            aria-describedby={count > 0 ? unreadCountId : undefined}
            icon={<FiBell />}
            variant="ghost"
            size="md"
          />
        </PopoverTrigger>
        {count > 0 && (
          <>
            <VisuallyHidden id={unreadCountId}>{t("{{count}} unread", { count })}</VisuallyHidden>
            <Box aria-hidden position="absolute" top="0" right="0" minW="18px" h="18px" px="1" rounded="full" bg="smAlertBadge" color="white" fontSize="2xs" fontWeight="bold" display="flex" alignItems="center" justifyContent="center" border="2px solid" borderColor="smBackground" pointerEvents="none">
              {count > 9 ? "9+" : count}
            </Box>
          </>
        )}
      </Box>
      <Portal>
        <PopoverContent ref={content} onBlur={keepFocusLostToNowhere(onClose)} w="auto" bg="smBackground" borderColor="smBorder" shadow="xl" _focus={{ outline: "none" }}>
          <PopoverBody p="0">
            <NotificationDropdown onClose={onClose} />
          </PopoverBody>
        </PopoverContent>
      </Portal>
    </Popover>
  );
};

const TOOLTIP = "[role='tooltip']";

const keepFocusLostToNowhere = (onClose: () => void) => (event: FocusEvent<HTMLElement>) => {
  if (event.relatedTarget !== null) return;
  event.preventDefault();
  const popover = event.currentTarget;
  const left = event.target;
  requestAnimationFrame(() => {
    if (left.isConnected && !left.matches(":disabled")) onClose();
    else if (!popover.contains(document.activeElement)) popover.focus();
  });
};
